import { InfoTooltip } from "@/components/InfoTooltip";

export type DurationUnit = "bd" | "cd";

interface Props {
  value: DurationUnit;
  onChange: (unit: DurationUnit) => void;
  /** Accessible label for the tablist (defaults to "Duration unit"). */
  label?: string;
}

// Plain-language clarification of the two units. The key subtlety: a business day is 8 working
// hours, a calendar day is 24 — so the same task can read *higher* in business days.
const UNIT_HELP =
  "Business days count working hours only (Mon–Fri, 9am–5pm ET — 8h = 1 day). " +
  "Calendar days count raw elapsed time (24h = 1 day). Because of the shorter business day, " +
  "a task worked mostly during business hours can show a higher number in business days than in " +
  "calendar days.";

/**
 * Business-days / calendar-days segmented toggle. Mirrors the markup the Request Detail screen
 * used inline; extracted so the Request List and Task Efficiency screens share one control.
 */
export function UnitToggle({ value, onChange, label = "Duration unit" }: Props) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <div className="unit-toggle" role="tablist" aria-label={label}>
        <button
          role="tab"
          aria-selected={value === "bd"}
          className={value === "bd" ? "on" : ""}
          onClick={() => onChange("bd")}
        >
          Business days
        </button>
        <button
          role="tab"
          aria-selected={value === "cd"}
          className={value === "cd" ? "on" : ""}
          onClick={() => onChange("cd")}
        >
          Calendar days
        </button>
      </div>
      <InfoTooltip text={UNIT_HELP} inline />
    </span>
  );
}
